<template>
  <div>
    <b-tabs v-model="selectedTab"
            card>

      <b-tab :title="evaluationTitle"
             active>
        <b-row align-h="between"
               class="container-fluid pb-3"
               no-gutters>

          <b-col cols="auto">
            <b-row id="filters">
              <b-col cols="auto">
                <b-button :pressed.sync="showFilter"
                          class="mb-2 mr-2">
                  FILTER
                </b-button>
              </b-col>

            </b-row>
          </b-col>
        </b-row>
        <b-table v-if="!helpersLoading"
                 :current-page="currentPage"
                 :fields="evaluationHelpers.evaluationTableFields"
                 :items="proposals"
                 :per-page="perPage"
                 :sort-by="'document_specification.document_id'"
                 :sort-desc='true'
                 class="table-responsive"
                 head-variant="light"
                 hover
                 responsive
                 striped
                 thead-class="text-center"
                 @row-clicked="setShowFinalSubmit">

          <template v-slot:top-row="{fields}">
            <proposal-filter :id="id"
                             v-model="proposals"
                             :fields="fields"
                             :not-include-key="['actions', 'samples', 'ordinalNumber']"
                             selected-call="None"/>
          </template>

          <template v-slot:cell(ordinalNumber)="row">
            <base-column-ordinal-number v-bind="{index: row.index, perPage, currentPage}"/>
          </template>

          <template v-slot:cell(samples[0].sample_safety)="row">
            {{ getSampleSafety(row.item.samples) }}
          </template>

          <template v-slot:cell(samples[0].sample_safety_radioactive)="row">
            <div v-if="hasProperty(row.item.samples[0], 'sample_safety_radioactive')">
              <b-form-checkbox v-model="row.item.samples[0].sample_safety_radioactive"
                               :disabled="true"/>
            </div>
            <div v-else>
              <b-form-checkbox :disabled="true"
                               v-bind:value="false"/>
            </div>
          </template>

          <template v-slot:cell(samples[0].safety_evaluation.status)="row">
            <traffic-lights-buttons v-model="row.item.samples[0].safety_evaluation.status"
                                    :options="trafficLightsOptions"
                                    disabled
                                    stacked/>
          </template>
          <template v-slot:cell(samples[0].radiation_evaluation.status)="row">
            <traffic-lights-buttons v-model="row.item.samples[0].radiation_evaluation.status"
                                    :options="trafficLightsOptions"
                                    disabled
                                    stacked/>
          </template>

          <template v-slot:cell(samples[0].safety_evaluation.states.name)="row">
            {{ row.item.samples[0].safety_evaluation.states.name }}
          </template>
          <template v-slot:cell(samples[0].radiation_evaluation.states.name)="row">
            {{ row.item.samples[0].radiation_evaluation.states.name }}
          </template>

          <template v-slot:cell(actions)="row">
            <div @click="setShowFinalSubmit(row.item, 0, $event)"
                 @keyup.down="setShowFinalSubmit(row.item, 0, $event)">
              <action-download-pdf :proposal="row.item"/>

              <template v-if="isSynchrotron">
                <base-icon-button-edit v-if="isEditionAllowed(row.item.samples[0])"
                                       v-b-modal.safetyRadiationModal
                                       title="Evaluate sample"
                                       @click="evaluateSample(row.item, 0)"/>

                <base-action-expand-details :b-table-row="row"/>
                <base-double-click-submit-button v-if="isEditionAllowed(row.item.samples[0])"
                                                 :btn-options="submitBtnOptions"
                                                 :is-confirmation="isFinalSubmit(row.item, 0)"
                                                 class="d-block ml-auto"
                                                 @click="submitEvaluation(row.item, row.item.samples[0])"/>
              </template>
              <template v-else>
                <base-action-expand-details :b-table-row="row"/>
              </template>

            </div>
          </template>

          <template v-slot:row-details="row">
            <b-card class="ma-4">
              <b-table id="samplesTable"
                       :fields="evaluationHelpers.evaluationTableFieldsSamples"
                       :items="row.item.samples"
                       :striped="false"
                       class="table-responsive"
                       hover
                       responsive
                       @row-clicked="setShowFinalSubmit">

                <template v-slot:cell(safety_evaluation.status)="samples">
                  <traffic-lights-buttons v-model="samples.item.safety_evaluation.status"
                                          :options="trafficLightsOptions"
                                          disabled
                                          stacked/>
                </template>
                <template v-slot:cell(radiation_evaluation.status)="samples">
                  <traffic-lights-buttons v-model="samples.item.radiation_evaluation.status"
                                          :options="trafficLightsOptions"
                                          disabled
                                          stacked/>
                </template>

                <template v-if="!isSynchrotron" v-slot:cell(actions)="samples">
                  <div @click="setShowFinalSubmit(row.item, samples.index, $event)"
                       @keyup.down="setShowFinalSubmit(row.item, samples.index, $event)">
                    <template v-if="isEditionAllowed(samples.item)">
                      <base-icon-button-edit v-b-modal.safetyRadiationModal
                                             title="Evaluate sample"
                                             @click="evaluateSample(row.item, samples.index)"/>
                      <base-double-click-submit-button :btn-options="submitBtnOptions"
                                                       :is-confirmation="isFinalSubmit(row.item, samples.index)"
                                                       @click="submitEvaluation(row.item, samples)"/>
                    </template>
                  </div>
                </template>

              </b-table>
            </b-card>
          </template>
        </b-table>
        <b-table-footer v-model="loading"
                        :current-page="currentPage"
                        :data-length="proposals.length"
                        :per-page="perPage"
                        @change-page="changePage">
          <template v-slot>
            <base-per-page-input v-model="perPage"/>
          </template>
        </b-table-footer>
        <b-modal id="safetyRadiationModal"
                 :ref="modalRef"
                 :title="evaluationTitle"
                 bg-variant="light"
                 header-bg-variant="dark"
                 header-text-variant="white"
                 hide-footer
                 lazy
                 size="lg"
                 @hidden="closeModal">
          <rapid-access-safety-radiation-modal v-model="sample"
                                               :fields="evaluationHelpers.evaluationModalFields"
                                               :isSafetyEvaluation="evaluationType === 'safety'"
                                               :proposal-id="proposalId"
                                               :sample-index="sampleIndex"
                                               @close-modal="closeModalSlot"/>
        </b-modal>
      </b-tab>

    </b-tabs>
  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import BaseActionExpandDetails from '@/components/actions/BaseActionExpandDetails';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseDoubleClickSubmitButton from '@/components/base-components/BaseDoubleClickSubmitButton';
  import BaseIconButtonEdit from '@/components/base-components/base-icons/BaseIconButtonEdit';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import BTableFooter from '@/components/BTableFooter';
  import options from '@/json/evaluation';
  import ProposalFilter from '@/components/ProposalFilter';
  import RapidAccessSafetyRadiationModal from '@/components/evaluation/rapid-access/RapidAccessSafetyRadiationModal';
  import submitBtnOptions from '@/json/doubleClickSubmitButton';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMarkSafety';
  import TrafficLightsButtons from '@/components/traffic_lights_buttons';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import {
    setDataInProposalsSafety,
    setDataInProposalsRadiation,
  } from '@/helpers/evaluation/rapid-access/rapidAccessSafetyRadiationEvaluation';

  export default {
    name: 'RapidAccessSafetyRadiationDashboard',
    components: {
      ActionDownloadPdf,
      BasePerPageInput,
      BaseActionExpandDetails,
      BaseColumnOrdinalNumber,
      BaseDoubleClickSubmitButton,
      BaseIconButtonEdit,
      RapidAccessSafetyRadiationModal,
      ProposalFilter,
      BTableFooter,
      TrafficLightsButtons,
    },
    mixins: [infrastructureMixin],
    props: {
      evaluationHelpers: {
        type: Object,
        required: true,
      },
      evaluationType: {
        type: String,
        required: true,
      },
      helpersLoading: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        // necessary for proposals loading
        id: 1,
        loading: false,
        proposals: [],
        // end

        // necessary for b-tabs and b-table feature
        selectedTab: 0,
        currentPage: 1,
        perPage: 20,
        // end

        // use only for visual effect and trigger event for filter component
        showFilter: true,

        // use to hide modal
        modalRef: 'modalRef',

        // necessary for editing and submitting evaluations
        proposalId: '',
        sampleIndex: 0,
        sample: {},
        options: options.options,
        finalSubmitProposalId: '',
        finalSubmitSampleIndex: '',
        submitBtnOptions,
        trafficLightsOptions,
      };
    },
    computed: {
      evaluation() {
        return `${this.evaluationType}_evaluation`;
      },
      evaluationTitle() {
        return `Rapid access ${this.evaluationType} evaluation`;
      },
      isSynchrotron() {
        return this.infrastructure === 'synchrotron';
      },
      isUserEditor() {
        const permissions = this.$store.getters['login/getLoggedUserAttribute']('permissions') || [];
        return permissions.includes(this.evaluationHelpers.editorPermission);
      },
    },
    watch: {
      showFilter() {
        this.$emit('toggle-show');
      },
    },
    created() {
      this.loadProposals();
    },
    methods: {
      changePage(val) {
        this.currentPage = val;
      },
      closeModal() {
        const index = this.proposals.findIndex((el) => this.getId(el) === this.proposalId);
        if (index >= 0) {
          this.proposals[index].samples[this.sampleIndex] = this.sample;
        }
        this.sample = {};
      },
      editable(sample) {
        if (sample[this.evaluation]?.states) {
          const values = ['submitted', 'deleted'];
          return (!values.includes(sample[this.evaluation].states.name));
        }
        return true;
      },
      evaluateSample(item, sampleIndex) {
        this.sampleIndex = sampleIndex;
        this.proposalId = this.getId(item);
        this.sample = item.samples[sampleIndex];
      },
      getSampleSafety(samples) {
        return samples[0].sample_safety?.join(', ');
      },
      isEditionAllowed(sample) {
        if (!this.isUserEditor) return false;
        return this.editable(sample);
      },
      isFinalSubmit(proposal, sampleIndex) {
        if (this.isSynchrotron === false) {
          return this.getId(proposal) === this.finalSubmitProposalId && sampleIndex === this.finalSubmitSampleIndex;
        }
        return this.getId(proposal) === this.finalSubmitProposalId;
      },
      async loadProposals() {
        this.loading = true;
        try {
          const response = await this.axios.get(
            `/documents/rapid-access/${this.evaluationType}/${this.infrastructure}`,
          );
          if (response.status !== 204) {
            this.proposals = response.data.documents;
            const { authors } = response.data;
            this.proposals = this.evaluationType === 'safety'
              ? setDataInProposalsSafety(response.data.documents, authors)
              : setDataInProposalsRadiation(response.data.documents, authors);
          } else {
            this.proposals = [];
          }
          // update data in proposal-filter component
          this.$emit('setup-proposal', this.proposals);
        } catch (error) {
          if (error.response.data) {
            this.$notify({ type: 'error', title: error.response.data });
          } else {
            this.$notify({ type: 'error', title: 'Communication error' });
          }
        } finally {
          this.loading = false;
        }
      },
      setShowFinalSubmit(proposal, sampleIndex, event) {
        this.finalSubmitProposalId = '';
        this.finalSubmitSampleIndex = '';
        if (event.target.id === this.submitBtnOptions.attempt.icon.id) {
          this.finalSubmitProposalId = this.getId(proposal);
          if (this.isSynchrotron === false) {
            this.finalSubmitSampleIndex = sampleIndex;
          }
        }
      },
      async submitEvaluation(item, sample) {
        const sampleItem = this.isSynchrotron ? sample : sample.item;
        const sampleIndex = this.isSynchrotron ? 0 : sample.index;
        if (this.submittable(sampleItem)) {
          const proposalId = this.getId(item);
          try {
            const response = await this.axios.patch(
              `/documents/rapid-access/${proposalId}/${this.evaluationType}/${this.infrastructure}`,
              { index: sampleIndex },
            );
            this.$notify({ type: 'success', title: response.data });
            const index = this.proposals.findIndex((el) => this.getId(el) === proposalId);
            this.proposals[index].samples[sampleIndex][this.evaluation].states.name = 'submitted';
          } catch (error) {
            if (error.response.data) {
              this.$notify({ type: 'error', title: error.response.data });
            } else {
              this.$notify({ type: 'error', title: 'Communication error' });
            }
          }
        }
      },
      submittable(sample) {
        const isDraft = this.getDeepProp(sample, `${this.evaluation}.states.name`) === 'draft';
        const isStatusSet = !!this.getDeepProp(sample, `${this.evaluation}.status`);
        if (!isDraft) {
          this.$notify({ type: 'error', title: 'Evaluation needs to be saved before submission!' });
          return isDraft;
        }
        if (!isStatusSet) {
          this.$notify({ type: 'error', title: 'Sample status needs to be set before submission!' });
          return isStatusSet;
        }
        return isDraft && isStatusSet;
      },
    },
  };
</script>

<style lang="scss"
       scoped>
  .card {
    margin: 4rem;
  }

  #filters {
    margin-top: 1rem;
  }

  .transparent-font {
    text-align: center;
  }

  .td-block {
    text-align: center;
    display: block !important;
  }

  .row {
    flex-wrap: inherit;
  }

  ::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    text-align: center !important;
  }

  ::-moz-placeholder { /* Firefox 19+ */
    text-align: center !important;
  }

  :-ms-input-placeholder { /* IE 10+ */
    text-align: center !important;
  }

  :-moz-placeholder { /* Firefox 18- */
    text-align: center !important;
  }
</style>
